import { CdkDrag, CdkDragDrop, CdkDragPreview, CdkDropList } from '@angular/cdk/drag-drop';
import { CdkContextMenuTrigger } from '@angular/cdk/menu';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  model,
  output,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FCanvasComponent, FFlowComponent, FFlowModule } from '@foblex/flow';
import { generateGuid } from '@foblex/utils';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideColumns2,
  lucideFolderSymlink,
  lucideGrid2x2,
  lucideLayoutDashboard,
  lucideNotebookText,
  lucideTrash,
} from '@ng-icons/lucide';
import { SidepanelService } from '@shared/components/sidepanel/sidepanel.service';

import { ClickDoubleDirective } from '@shared/directives/click-double.directive';
import { CELL_SIZE } from '../../shared/models/noeud.constants';
import { ContextMenuComponent, MenuItem } from '../components/context-menu/context-menu.component';
import { ImportModuleFooterComponent } from '../components/import-module-footer/import-etape-footer.component';
import { ImportModuleComponent } from '../components/import-module/import-module.component';
import { Connecteur } from '../models/connecteur.model';
import { Module } from '../models/module.model';
import { Étape } from '../models/étape.model';
import { TYPE_ÉLÉMENT } from '../parcours-detail/parcours-detail.component';
import { ModuleStore } from '../parcours-module/store/module.store';
import { ParcoursStore } from '../store/parcours.store';
import { LivrablePrincipal } from '@shared/models/livrable-principal.enum';
import { CanEditService } from '@shared/services/can-edit.service';
import { Référentiel } from '../../administration/gestion-référentiels/models/référentiel.model';
import { Cartographie } from '../../cartographie/models/cartographie.model';

export type CanvasGroup = Étape & {
  position: { x: number; y: number };
  size: { width: number; height: number };
  modules: Module[];
};

@Component({
  selector: 'app-canvas-renderer',
  imports: [
    FFlowModule,
    CdkDropList,
    CdkDrag,
    CdkDragPreview,
    CdkContextMenuTrigger,
    ContextMenuComponent,
    NgIcon,
    ClickDoubleDirective,
  ],
  templateUrl: './canvas-renderer.component.html',
  styleUrls: ['./canvas-renderer.component.scss', '../référentiel-métier-parcours.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideIcons({
      lucideGrid2x2,
      lucideLayoutDashboard,
      lucideNotebookText,
      lucideColumns2,
      lucideTrash,
      lucideFolderSymlink,
    }),
  ],
})
export class CanvasRendererComponent {
  readonly moduleStore = inject(ModuleStore);
  readonly #parcoursStore = inject(ParcoursStore);

  readonly #router = inject(Router);
  readonly #route = inject(ActivatedRoute);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #canEditService = inject(CanEditService);

  readonly groupIds = computed(() => this.groups()?.map((g) => g.id) ?? []);
  readonly filteredGroupeConnecteurs = computed(() =>
    this.groupeConnecteurs()?.filter(
      (c) =>
        ((c.idEtapePrecedente ? this.groupIds().includes(c.idEtapePrecedente) : false) ||
          (c.idGroupeEtapesALaCartePrecedent ? this.groupIds().includes(c.idGroupeEtapesALaCartePrecedent) : false)) &&
        (c.idEtapeSuivante ? this.groupIds().includes(c.idEtapeSuivante) : false) &&
        (c.idGroupeEtapesALaCarteSuivant ? this.groupIds().includes(c.idGroupeEtapesALaCarteSuivant) : false),
    ),
  );

  readonly id = model<string | null>();
  readonly type = input<TYPE_ÉLÉMENT | null>();

  readonly groups = input.required<CanvasGroup[]>();
  readonly groupTemplate = input.required<TemplateRef<{ $implicit: Étape }>>();
  readonly groupeConnecteurs = input<Connecteur[]>();
  readonly cartographie = input.required<Cartographie>();
  readonly referentiel = input.required<Référentiel>();
  readonly roles = input.required<string[]>();

  readonly peutModifier = computed(() =>
    this.#canEditService.check(
      this.referentiel().equipeResponsable,
      this.roles(),
      LivrablePrincipal.MODULARISATION,
      this.cartographie(),
      this.referentiel(),
    ),
  );

  readonly onGroupSelection = output<(string | null)[]>();
  readonly onNodeSelection = output<(string | null)[]>();
  readonly onModuleOrderChange = output<Module>();
  readonly onModuleDelete = output<string>();

  public fCanvas = viewChild.required(FCanvasComponent);
  public fFlow = viewChild.required(FFlowComponent);

  readonly modulesMapByEtapeId = input<Record<string, Module[]>>();
  protected readonly CELL_SIZE = CELL_SIZE;

  protected nodeContextMenuItems(type: TYPE_ÉLÉMENT) {
    const result: MenuItem<Module | Étape>[] = [
      {
        label: 'Afficher les détails',
        icon: 'lucideNotebookText',
        action: (moduleOuEtape: Module | Étape | undefined) => {
          if (!moduleOuEtape) {
            console.warn('Module/step is undefined, cannot navigate to scénarisation.');
            return;
          }
          this.#router.navigate([], {
            queryParams: { detail: true, id: moduleOuEtape.id, type: type },
            queryParamsHandling: 'merge',
          });
        },
      },
    ];

    if (this.peutModifier()) {
      if (type == 'étape') {
        result.push(
          {
            label: 'Ajouter un module',
            icon: 'lucideLayoutDashboard',
            action: (etape: Module | Étape | undefined) => {
              if (!etape) {
                console.warn('step is undefined, cannot navigate to scénarisation.');
                return;
              }
              this.#ajouterModule(etape);
            },
          },
          {
            label: 'Importer un module',
            icon: 'lucideFolderSymlink',
            action: (module: Module | Étape | undefined) => {
              if (!module) {
                console.warn('step is undefined, cannot navigate to scénarisation.');
                return;
              }
              this.#importerModule(module);
            },
          },
        );
      }

      if (type === 'module') {
        result.push(
          {
            label: 'Scénariser',
            icon: 'lucideColumns2',
            action: (module: Module | Étape | undefined) => {
              if (!module) {
                console.warn('Module is undefined, cannot navigate to scénarisation.');
                return;
              }
              this.#router.navigate(['modules', module.id], { relativeTo: this.#route });
            },
          },
          {
            label: 'Supprimer',
            icon: 'lucideTrash',
            action: (module: Module | Étape | undefined) => {
              if (!module) return;
              this.#supprimerModule(module.id);
            },
          },
        );
      }
    }

    return result;
  }

  zoom = 1;
  currentPosition = { x: 0, y: 0 };

  orderedGroups(modules: Module[]) {
    if (!modules) return;
    return modules.sort((a, b) => a.indice - b.indice);
  }

  onLoaded() {
    const id = this.id();
    const type = this.type();

    if (id && type === 'étape') {
      this.fCanvas().centerGroupOrNode(id);
    } else {
      this.#fitToScreen();
    }
  }

  onDrop(event: CdkDragDrop<Module[]>, group: CanvasGroup): void {
    const newModule = this.#moveModule(event.previousIndex, event.currentIndex, group.modules);
    this.onModuleOrderChange.emit(newModule);
  }

  onZoom(event: { scale: number }) {
    this.zoom = event.scale;
  }

  onDragMoved(event: any) {
    const { x, y } = event.pointerPosition || { x: 0, y: 0 };
    // Center the position by subtracting half the width and height of the module
    this.currentPosition.x = x - 109 / 2;
    this.currentPosition.y = y - 49 / 2;
  }

  onModuleClick(id: string | null) {
    this.fFlow().clearSelection();
    this.onNodeSelection.emit([id]);
  }

  onEtapeClick(id: string | null) {
    this.fFlow().clearSelection();
    this.onGroupSelection.emit([id]);
  }

  #ajouterModule(etape: Étape | Module) {
    const idEtapeSelectionnée = etape.id;

    if (!idEtapeSelectionnée) {
      console.error('Aucune étape sélectionnée pour ajouter un module.');
      return;
    }
    const indiceModule = this.#findUltimateModuleIndex(idEtapeSelectionnée) + 1;

    const id = generateGuid().replaceAll('-', '');
    this.moduleStore.ajouterUnModule({
      id,
      nom: `Module ${indiceModule.toString().padStart(2, '0')} - intitulé`,
      idEtape: idEtapeSelectionnée,
      indice: indiceModule,
    });

    this.#parcoursStore.sauvegarderParcours();
  }

  #importerModule(etape: Étape | Module) {
    const idEtapeSelectionnée = etape.id;

    if (!idEtapeSelectionnée) {
      console.error('Aucune étape sélectionnée pour ajouter un module.');
      return;
    }
    const indiceModule = this.#findUltimateModuleIndex(idEtapeSelectionnée) + 1;

    this.#sidepanelService.setComponentToDisplay(
      'Récupérer un module',
      ImportModuleComponent,
      "Vous pouvez récupérer un module d'un autre Parcours.",
      {
        etape: etape as Étape,
      },
      ImportModuleFooterComponent,
    );
    this.#sidepanelService.open();
  }

  #findUltimateModuleIndex(idEtape: string): number {
    const modulesMapByEtapeId = this.modulesMapByEtapeId();
    if (!modulesMapByEtapeId) return 0;

    const modules = modulesMapByEtapeId[idEtape];
    if (!modules || modules.length === 0) return 0;

    return Math.max(...modules.map((m) => m.indice));
  }

  #moveModule(fromIndex: number, toIndex: number, previousModules: Module[]): Module {
    const array = previousModules;
    const target = array[fromIndex];
    const delta = toIndex < fromIndex ? -1 : 1;

    for (let i = fromIndex; i !== toIndex; i += delta) {
      array[i] = array[i + delta];
      array[i].indice = i; // Update the index of the module
    }

    array[toIndex] = target;
    array[toIndex].indice = toIndex; // Update the index of the module
    return array[toIndex];
  }

  #fitToScreen(): void {
    this.fCanvas().fitToScreen({ x: 550, y: 50 }, true);
  }

  #supprimerModule(idModule: string) {
    const module = this.moduleStore.entityMap()[idModule];
    if (!module) return;

    this.onModuleDelete.emit(idModule);
  }

  onDoubleClick(id: string) {
    if (!this.#sidepanelService.isOpen()) {
      // On ne recentre le canva sur l'élément que si le sidepanel n'était pas encore ouvert
      // Ca permet de le garder à l'écran
      this.fCanvas().centerGroupOrNode(id.replaceAll('-', ''));
    }
    this.#router.navigate([], { queryParams: { detail: true }, queryParamsHandling: 'merge' });
  }
}
